<script setup>
import { useForm, router } from '@inertiajs/vue3'
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiCard from '@/Components/UI/UiCard.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import StickyActionBar from '@/Components/UI/StickyActionBar.vue'
import { useCurrency } from '@/composables/useCurrency.js'

defineOptions({ layout: AppLayout })

const props = defineProps({
  rental: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
})

const now = new Date()
const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
                   .toISOString().slice(0, 16)

const form = useForm({
  actual_end_at:       localNow,
  extra_charges_cents: 0,
  extra_notes:         '',
  status:              'returned',
})

const { t } = useI18n()
const { formatMoney, formatDateTime, setting } = useCurrency()

// Open-damage selection state — local UI only; the resulting cents land in form.extra_charges_cents
const damageBills = ref({})

watch(() => props.rental?.open_damages, (list) => {
  damageBills.value = {}
  ;(list || []).forEach(d => {
    damageBills.value[d.id] = { selected: false, amount_cents: d.net_cost_cents }
  })
}, { immediate: true })

const damageBillTotalCents = computed(() => {
  return Object.values(damageBills.value)
    .filter(b => b.selected)
    .reduce((sum, b) => sum + (b.amount_cents || 0), 0)
})

watch(damageBillTotalCents, (cents) => {
  form.extra_charges_cents = cents
})

const setDamageSelected = (id, sel) => {
  damageBills.value = { ...damageBills.value, [id]: { ...damageBills.value[id], selected: sel } }
}

const setDamageAmount = (id, dollars) => {
  const cents = Math.round(parseFloat(dollars) * 100) || 0
  damageBills.value = { ...damageBills.value, [id]: { ...damageBills.value[id], amount_cents: cents } }
}

const extraDollars = computed({
  get: () => (form.extra_charges_cents / 100).toFixed(2),
  set: (v) => { form.extra_charges_cents = Math.round(parseFloat(v) * 100) || 0 },
})

const finalTotalCents = computed(() => {
  if (!form.actual_end_at) return 0
  const days = Math.max(
    Math.ceil((new Date(form.actual_end_at) - new Date(props.rental.start_at)) / 86400000), 1
  )
  return days * props.rental.daily_rate_cents + form.extra_charges_cents
})

const balanceCents = computed(() => finalTotalCents.value - (props.rental.amount_paid_cents || 0))

const moneyAdvice = computed(() => {
  const b = balanceCents.value
  if (b > 0) return { type: 'collect', tone: 'warn',   msg: `Collect ${formatMoney(b)} from customer` }
  if (b < 0) return { type: 'refund',  tone: 'accent', msg: `Refund ${formatMoney(Math.abs(b))} to customer` }
  return              { type: 'paid',    tone: 'ok',     msg: 'Paid in full — no money to collect or return' }
})

const toneStyle = computed(() => {
  const t = moneyAdvice.value.tone
  return {
    background: `color-mix(in oklch, var(--${t}) 10%, var(--surface))`,
    borderColor: `color-mix(in oklch, var(--${t}) 35%, var(--line))`,
    color: `var(--${t})`,
  }
})

const isLate = computed(() => {
  return form.actual_end_at && new Date(form.actual_end_at) > new Date(props.rental.expected_end_at)
})

function submit() {
  form.transform((data) => ({ rental: data }))
      .patch(`/rentals/${props.rental.id}/process_return`, { replace: true })
}
</script>

<template>
  <PageShell
    narrow
    title="Finish Rental"
    :subtitle="`${rental.vehicle_name} — ${rental.customer_name}`"
    :back-href="`/rentals/${rental.id}`"
    :back-label="`Back to Rental #${rental.id}`"
  >
    <form @submit.prevent="submit">
      <UiCard>
        <div class="space-y-5">
          <div>
            <label class="block text-sm font-medium text-[color:var(--ink)] mb-1.5">Actual Return Date &amp; Time *</label>
            <UiInput
              v-model="form.actual_end_at"
              type="datetime-local"
              :error="errors.actual_end_at"
            />
            <p
              v-if="isLate"
              class="mt-1.5 text-xs font-medium text-[color:var(--warn)]"
            >
              Late return — vehicle was due back {{ formatDateTime(rental.expected_end_at) }}
            </p>
          </div>

          <div v-if="rental.open_damages?.length">
            <label class="block text-sm font-medium text-[color:var(--ink)] mb-1.5">{{ t('damage.openOnRental') }}</label>
            <ul class="space-y-2 mb-2">
              <li
                v-for="d in rental.open_damages"
                :key="d.id"
                class="flex items-center gap-2"
              >
                <input
                  type="checkbox"
                  :checked="damageBills[d.id]?.selected"
                  @change="setDamageSelected(d.id, $event.target.checked)"
                >
                <span class="flex-1 text-sm truncate text-[color:var(--ink)]">{{ d.description }}</span>
                <UiInput
                  type="number"
                  step="0.01"
                  class="w-32"
                  :model-value="((damageBills[d.id]?.amount_cents ?? 0) / 100).toFixed(2)"
                  @update:model-value="v => setDamageAmount(d.id, v)"
                />
              </li>
            </ul>
            <p class="text-xs text-[color:var(--muted)]">
              {{ t('damage.return.prefillNote') }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-[color:var(--ink)] mb-1.5">
              Extra Charges ({{ setting.currency_code }})
            </label>
            <UiInput
              v-model="extraDollars"
              type="number"
              hint="Late fees, damage, fuel, etc."
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-[color:var(--ink)] mb-1.5">Extra Charges Note</label>
            <UiTextarea
              v-model="form.extra_notes"
              :rows="2"
              placeholder="Describe any extra charges…"
            />
          </div>

          <div
            class="rounded-[var(--radius-lg)] border p-4 text-sm space-y-1.5"
            :style="toneStyle"
          >
            <div class="flex justify-between">
              <span>Final total</span>
              <span class="fw-value">{{ formatMoney(finalTotalCents) }}</span>
            </div>
            <div class="flex justify-between">
              <span>Already paid</span>
              <span class="fw-value">{{ formatMoney(rental.amount_paid_cents || 0) }}</span>
            </div>
            <div class="border-t border-current/20 pt-1.5 flex justify-between fw-value text-base">
              <span>
                {{ moneyAdvice.type === 'collect' ? 'Still owed' : moneyAdvice.type === 'refund' ? 'Overpaid by' : 'Balance' }}
              </span>
              <span>{{ formatMoney(Math.abs(balanceCents)) }}</span>
            </div>
            <p class="pt-1 font-medium">
              → {{ moneyAdvice.msg }}
            </p>
          </div>
        </div>
      </UiCard>

      <StickyActionBar>
        <template #info>
          <span class="text-xs text-[color:var(--muted)] truncate">{{ moneyAdvice.msg }}</span>
        </template>
        <UiButton
          variant="secondary"
          @click="router.visit(`/rentals/${rental.id}`)"
        >
          Cancel
        </UiButton>
        <UiButton
          type="submit"
          variant="primary"
          :loading="form.processing"
        >
          Confirm Return
        </UiButton>
      </StickyActionBar>
    </form>
  </PageShell>
</template>
